import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crown, Trophy, Medal, MessageCircle, Copy, Users, MapPin, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PODIUM_STYLES = [
  { ring: "#F5D061", glow: "rgba(245,208,97,0.45)", label: "#1", icon: Crown },
  { ring: "#B4B4C0", glow: "rgba(180,180,192,0.35)", label: "#2", icon: Trophy },
  { ring: "#FF4D6D", glow: "rgba(255,77,109,0.4)", label: "#3", icon: Medal },
];

function PodiumCard({ entry, place }) {
  const s = PODIUM_STYLES[place];
  const Icon = s.icon;
  return (
    <div className={`glass !rounded-3xl p-5 text-center relative ${place === 0 ? "md:-translate-y-3" : ""}`}
      style={{ borderColor: s.ring, boxShadow: `0 0 26px ${s.glow}` }}
      data-testid={`podium-${place + 1}`}>
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full font-mono text-[10px] font-bold uppercase tracking-widest"
        style={{ background: s.ring, color: "#0A0A0A" }}>{s.label}</div>
      <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-3 overflow-hidden"
        style={{ border: `2px solid ${s.ring}`, background: "rgba(255,255,255,0.05)" }}>
        {entry.profile_picture
          ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${entry.profile_picture}`} alt="" className="w-full h-full object-cover" />
          : <Icon className="w-7 h-7" style={{ color: s.ring }} />}
      </div>
      <div className="font-display text-lg leading-tight">{entry.name}</div>
      {entry.city && <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-muted inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-magenta" /> {entry.city}</div>}
      <div className="mt-2 font-mono font-bold text-2xl text-lime">{entry.conversions}</div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-white/50">friends brought</div>
    </div>
  );
}

export default function LeaderboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/referrals/leaderboard${city ? `?city=${encodeURIComponent(city)}` : ""}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Couldn't load the leaderboard"))
      .finally(() => setLoading(false));
  }, [city]);

  const list = data?.leaderboard || [];
  const top3 = list.slice(0, 3);
  const rest = list.slice(3);
  const me = data?.me;
  const myLink = me?.link || "";

  const shareWhatsApp = () => {
    const text = me?.rank
      ? `I'm #${me.rank} on the Eventom squad leaderboard 🏆 Join with my code ${me.code} and let's climb: ${myLink}`
      : `Join my squad on Eventom Use my code ${me?.code || ""} for ₹100 off your first ticket: ${myLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  return (
    <Layout>
      {/* THEMED PURPLE STRIP — magazine signature for leaderboard */}
      <section className="themed-purple sticker-wall py-10 overflow-hidden" data-testid="lb-themed-strip">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/80 mb-2">● Squad leaderboard</div>
          <h1 className="font-fancy uppercase text-white leading-[0.9]" style={{ fontSize: "clamp(44px, 7vw, 88px)", textShadow: "3px 3px 0 rgba(0,0,0,0.3)" }}>
            Who's got the <span className="text-striped">biggest squad?</span>
          </h1>
          <p className="mt-3 text-white/90 max-w-md text-sm">Every friend who books their first ticket with your code counts. Top recruiters per city — updated live.</p>
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 relative">
        <div className="blob blob-violet blob-float" style={{ width: 300, height: 300, top: "-4%", right: "-6%" }} />
        <div className="chip mb-3"><Trophy className="w-3 h-3" /> Squad Leaderboard</div>
        <h1 className="font-display hero-tilt text-4xl sm:text-5xl lg:text-6xl text-white">
          Who's bringing<br />the <span className="text-lime" style={{ textShadow: "0 0 34px rgba(170,238,0,0.4)" }}>biggest squad?</span>
        </h1>
        <p className="mt-4 text-white/60 max-w-lg">Every friend who books their first ticket with your code counts. Top recruiters per city — updated live.</p>

        {/* City filter chips */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="leaderboard-city-filter">
          <button onClick={() => setCity("")} data-testid="lb-city-all"
            className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${!city ? "bg-lime text-ink" : "bg-surface text-muted hover:text-white"}`}>All India</button>
          {(data?.cities || []).map((c) => (
            <button key={c} onClick={() => setCity(c)} data-testid={`lb-city-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${city === c ? "bg-lime text-ink" : "bg-surface text-muted hover:text-white"}`}>{c}</button>
          ))}
        </div>

        {/* My rank card */}
        {user && me && (
          <div className="mt-6 card-purple p-5 flex flex-wrap items-center justify-between gap-4" data-testid="leaderboard-me-card">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-purple mb-1">Your standing</div>
              <div className="font-display text-2xl">
                {me.rank ? <>You're <span className="text-lime">#{me.rank}</span> in India</> : "You're not on the board yet"}
                <span className="text-white/60 text-base font-body font-normal"> · {me.conversions} converted</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={shareWhatsApp} data-testid="leaderboard-share-whatsapp" className="btn-lime !py-2.5 !px-5 !text-sm"><MessageCircle className="w-4 h-4" /> Recruit on WhatsApp</button>
              <button onClick={async () => { try { await navigator.clipboard.writeText(myLink); toast.success("Referral link copied"); } catch { toast(myLink); } }}
                data-testid="leaderboard-copy-link" className="btn-ghost !py-2.5 !px-4 !text-sm"><Copy className="w-4 h-4" /> Copy link</button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="mt-10 glass rounded-3xl p-12 text-center font-mono text-sm text-muted">Loading the standings…</div>
        ) : list.length === 0 ? (
          <div className="mt-10 glass rounded-3xl p-12 text-center" data-testid="leaderboard-empty">
            <Sparkles className="w-10 h-10 text-lime mx-auto mb-3" />
            <div className="font-display text-2xl">No squads on the board{city ? ` in ${city}` : ""} — yet.</div>
            <p className="text-white/60 mt-2 text-sm">Share your referral code, get a friend to book their first ticket, and claim the #1 spot.</p>
            {user ? (
              me && <button onClick={shareWhatsApp} className="btn-lime mt-5"><MessageCircle className="w-4 h-4" /> Start Recruiting</button>
            ) : (
              <Link to="/signup" data-testid="leaderboard-signup-cta" className="btn-lime mt-5 inline-flex">Join Eventom & Get Your Code</Link>
            )}
          </div>
        ) : (
          <>
            {/* Podium */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:items-end" data-testid="leaderboard-podium">
              {top3[1] && <div className="md:order-1"><PodiumCard entry={top3[1]} place={1} /></div>}
              {top3[0] && <div className="md:order-2"><PodiumCard entry={top3[0]} place={0} /></div>}
              {top3[2] && <div className="md:order-3"><PodiumCard entry={top3[2]} place={2} /></div>}
            </div>

            {/* Ranked list */}
            {rest.length > 0 && (
              <div className="mt-6 glass rounded-3xl overflow-hidden" data-testid="leaderboard-list">
                {rest.map((e) => (
                  <div key={e.user_id} data-testid={`leaderboard-row-${e.rank}`}
                    className={`flex items-center gap-4 px-5 py-3.5 border-b border-white/5 last:border-0 ${me && e.user_id === me.user_id ? "bg-lime/10" : "hover:bg-white/5"} transition-colors`}>
                    <div className="w-9 font-mono font-bold text-white/50">#{e.rank}</div>
                    <div className="w-9 h-9 rounded-full overflow-hidden border border-white/15 bg-white/5 flex items-center justify-center shrink-0">
                      {e.profile_picture
                        ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${e.profile_picture}`} alt="" className="w-full h-full object-cover" />
                        : <Users className="w-4 h-4 text-white/40" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold text-sm truncate">{e.name}</div>
                      {e.city && <div className="text-[10px] font-mono uppercase tracking-widest text-muted">{e.city}</div>}
                    </div>
                    <div className="font-mono font-bold text-lime">{e.conversions}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* How it works */}
        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          {[
            { n: "01", t: "Share your code", d: "Grab your unique link from your profile — or right here." },
            { n: "02", t: "Friend books a ticket", d: "They get ₹100 off their first booking with your code." },
            { n: "03", t: "You climb the board", d: "Each converted friend = +100 points and a step up the ranks." },
          ].map((s) => (
            <div key={s.n} className="glass !rounded-2xl p-5">
              <div className="font-mono text-lime text-xs font-bold">{s.n}</div>
              <div className="font-heading font-bold mt-1">{s.t}</div>
              <div className="text-xs text-white/55 mt-1">{s.d}</div>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}
